import type { AgentSessionStatus, AgentStreamEventV1 } from '@/model/Agent.model';
import {
  type ActivityRow,
  type ActivityRowMap,
  applyActivityEvent,
  isTerminalStatus,
  toActivitySnapshot,
} from './ActivityRow.utils';

const ROWS_CHANNEL = 'rows';

/**
 * Owns the derived Activity rows for one session. Fed raw events (live via the transport, one at a
 * time, or in bulk from `GET /events` history) plus the authoritative session status; holds the
 * ordered row map incrementally and exposes a materialised snapshot BY STABLE REFERENCE so
 * `useSyncExternalStore` bails out correctly (no render loop). Mirrors StepFormEngine /
 * GraphCanvasEngine: a domain engine that owns derived state, so the transport stays generic and the
 * panel stays a pure renderer.
 *
 * Each live `ingest` applies exactly one event to the row map (O(1) amortised) rather than re-folding
 * the whole history — the transport holds no event buffer, and neither does this engine. Both the live
 * `ingest` and the bulk `ingestAll` apply events through the single `applyActivityEvent` helper, so
 * there is no duplicated switch. A snapshot is rebuilt (and subscribers notified) only when an event or
 * a status change actually mutates the rows.
 */
export class AgentActivityEngine {
  readonly #eventTarget = new EventTarget();
  readonly #rowMap: ActivityRowMap = new Map();
  #status: AgentSessionStatus | undefined;
  #snapshot: Array<ActivityRow> = [];

  /** Applies one live event; rebuilds the snapshot and notifies only if it changed anything. */
  ingest(event: AgentStreamEventV1): void {
    if (applyActivityEvent(this.#rowMap, event)) {
      this.#publish();
    }
  }

  /** Applies a batch (e.g. GET /events history), then publishes once. */
  ingestAll(events: ReadonlyArray<AgentStreamEventV1>): void {
    events.forEach((event) => applyActivityEvent(this.#rowMap, event));
    this.#publish();
  }

  /**
   * Sets the authoritative session status; republishes only when it actually changes. Terminal state
   * is MONOTONIC — once the session has resolved (completed/failed/cancelled) it can never regress to a
   * non-terminal status. This guards against a stale or coalesced `/tree` reconcile (which may have
   * fetched a pre-terminal `running` snapshot) overwriting a terminal status and re-spinning resolved
   * rows. A terminal→different-terminal transition is still allowed (shouldn't happen, but harmless).
   */
  setStatus(status: AgentSessionStatus): void {
    if (this.#status === status) {
      return;
    }
    if (isTerminalStatus(this.#status) && !isTerminalStatus(status)) {
      return;
    }
    this.#status = status;
    this.#publish();
  }

  /** Ref-stable snapshot for useSyncExternalStore — a new array only when rows actually changed. */
  getRows(): ReadonlyArray<ActivityRow> {
    return this.#snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.#eventTarget.addEventListener(ROWS_CHANNEL, listener);
    return () => this.#eventTarget.removeEventListener(ROWS_CHANNEL, listener);
  }

  #publish(): void {
    this.#snapshot = toActivitySnapshot(this.#rowMap, isTerminalStatus(this.#status));
    this.#eventTarget.dispatchEvent(new Event(ROWS_CHANNEL));
  }
}
